import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import _ from 'lodash';
import { AgentListFilter } from 'src/app/samwebDescription/samweb/agentListFilterType';
import { AgentListEntryRemoteDto } from 'src/generated/signalR/agent-list-entry-remote-dto';
import { UserStatusInfoRemoteDto } from 'src/generated/signalR/user-status-info-remote-dto';
import { SamwinUserShortInfoRemoteDto } from 'src/generated/swagger';
import { SessionState } from '../../session/session.state';
import { EmailContact } from '../email/email-behaviour/email-contact';
import { SamwinUserConfigurationState } from '../samwin-user-configuration/samwin-user-configuration.state';
import { AgentListUpdated } from './samwin-agent-list.actions';

export interface SamwinAgentListStateModel {
   agents: AgentListEntryRemoteDto[];
}

@State<SamwinAgentListStateModel>({
   name: 'samwinAgentList',
   defaults: {
      agents: [],
   },
})
@Injectable()
export class SamwinAgentListState {
   @Selector([SamwinAgentListState])
   public static getAgentList(state: SamwinAgentListStateModel): AgentListEntryRemoteDto[] {
      return state.agents;
   }

   @Selector([SamwinAgentListState.getAgentList])
   public static getAgentListWithEmailAddress(agentList: AgentListEntryRemoteDto[]): AgentListEntryRemoteDto[] {
      return agentList.filter((agent) => !_.isEmpty(agent?.email?.trim()));
   }

   @Selector([SamwinAgentListState.getAgentDetailsForUserId])
   public static getAgentGroupIDsForUserId(getAgentDetails: (userId: number) => AgentListEntryRemoteDto) {
      return (userId: number) => {
         return getAgentDetails(userId)?.memberOfGroups ?? [];
      };
   }

   @Selector([SamwinAgentListState.getAgentListWithEmailAddress, SessionState.getAuthenticatedUserId, SamwinAgentListState.getAgentGroupIDsForUserId])
   public static getAgentListOfOwnGroup(
      agents: AgentListEntryRemoteDto[],
      ownUser: number,
      getGroupsFromID: (userId: number) => number[]
   ): AgentListEntryRemoteDto[] {
      const ownUserGroups = getGroupsFromID(ownUser);
      return agents.filter((agent) => _.some(_.intersection(agent.memberOfGroups, ownUserGroups)));
   }

   @Selector([SamwinAgentListState.getAgentList])
   public static getAgentDetailsForUserId(agentList: AgentListEntryRemoteDto[]): (userId: number) => null | AgentListEntryRemoteDto {
      return (userId: number) => {
         return agentList.find((agent) => agent.userId === userId);
      };
   }

   @Selector([SamwinAgentListState.getAgentList])
   public static getCurrentStatusInfoFn(agentList: AgentListEntryRemoteDto[]): (userId: number) => null | UserStatusInfoRemoteDto {
      return (userId: number) => {
         return agentList.find((agent) => agent.userId === userId)?.userStatus;
      };
   }

   @Selector()
   public static getPossibleAgentListFilters() {
      return AgentListFilter.values();
   }

   @Selector([SamwinAgentListState.getAgentListOfOwnGroup, SamwinUserConfigurationState.getUserConfigurationFn])
   public static getAgentListWithConfiguration(
      agents: AgentListEntryRemoteDto[],
      getUserConfigurationFn: (userId: number) => SamwinUserShortInfoRemoteDto
   ): SamwinUserShortInfoRemoteDto[] {
      return agents.map((agent) => getUserConfigurationFn(agent.userId)).filter((config) => !_.isNil(config));
   }

   @Selector([SamwinAgentListState.getAgentListWithConfiguration])
   public static getAgentListEmails(agents: SamwinUserShortInfoRemoteDto[]): EmailContact[] {
      return agents.map((agent) => new EmailContact(agent.email, agent.displayName)) ?? [];
   }

   @Action(AgentListUpdated)
   public agentListUpdate(ctx: StateContext<SamwinAgentListStateModel>, action: AgentListUpdated) {
      ctx.patchState({
         agents: action.data,
      });
   }
}
